import { useUserStore } from '@/store/user'
import { appLogout } from '@/utils/logout'

const TOKEN_TIMEOUT_MS = 10_000

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Auth token request timed out after ${ms}ms`)), ms)
    ),
  ])
}

export async function getBearerToken(): Promise<string> {
  const userStore = useUserStore()
  const authMethod = userStore.getAuthMethod

  // An SSO user whose Auth0 token can't be obtained on this origin must re-auth via SSO.
  // Never fall back to the Cognito session: it belongs to a different identity and the
  // backend answers a misleading "Usuario no existe" (Galgo / Jennifer Pava, 2026-09-28,
  // whose orphan Cognito account shadowed her SSO login). The per-origin Auth0 session is
  // fragile because the tenant has no custom domain (finnecto.us.auth0.com is a third
  // origin), so getAccessTokenSilently can throw MissingRefreshTokenError here.
  if (authMethod === 'auth0') {
    const { getAuth0Client } = await import('./auth0Client')
    const auth0 = getAuth0Client()
    if (auth0) {
      const orgId = auth0.idTokenClaims?.value?.org_id as string | undefined
      try {
        const sdkToken: string = await withTimeout(auth0.getAccessTokenSilently({
          detailedResponse: false,
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
            ...(orgId && { organization: orgId }),
          }
        }), TOKEN_TIMEOUT_MS)
        if (sdkToken) return sdkToken
      } catch (_e) {
        // handled below: SSO re-auth, not a Cognito fallback
      }
    }
    await appLogout()
    throw new Error('Auth0 session unavailable')
  }

  const { Auth } = await import('aws-amplify')
  const session = await withTimeout(Auth.currentSession(), TOKEN_TIMEOUT_MS)
  return session.getAccessToken().getJwtToken()
}
